import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { extractApiError } from "@/lib/api";
import { AppShell } from "@/components/AppShell";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";
import {
  BarChart3, ShieldCheck, DollarSign, FileText, Users, Plus,
  Beaker, ArrowLeft, Sparkles, Package,
} from "lucide-react";

function Section({ id, title, icon: Icon, children }) {
  return (
    <section id={id} data-testid={`m5admin-section-${id}`} className="mt-10">
      <h2 className="text-2xl font-heading font-medium text-fg-primary flex items-center gap-2">
        {Icon && <Icon className="h-5 w-5" aria-hidden="true" />}
        {title}
      </h2>
      <div className="mt-3">{children}</div>
    </section>
  );
}

function Stat({ label, value, testid }) {
  return (
    <div data-testid={testid} className="rounded-lg border border-border-subtle bg-bg-surface p-4">
      <p className="text-xs uppercase tracking-wider text-fg-secondary">{label}</p>
      <p className="mt-1 text-2xl font-heading font-medium text-fg-primary">{value}</p>
    </div>
  );
}

export default function AdminM5() {
  const [overview, setOverview] = useState(null);
  const [plans, setPlans] = useState([]);
  const [ents, setEnts] = useState([]);
  const [entSummary, setEntSummary] = useState([]);
  const [costs, setCosts] = useState(null);
  const [finance, setFinance] = useState(null);
  const [financeInputs, setFinanceInputs] = useState([]);
  const [policies, setPolicies] = useState([]);
  const [contentInfo, setContentInfo] = useState(null);
  const [sample, setSample] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState(() => {
    const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
  });
  const [newInput, setNewInput] = useState({ category: "revenue", value: "", note: "" });
  const [newPolicy, setNewPolicy] = useState({ policy_type: "terms", version: "", content: "" });

  const load = async () => {
    setError(null);
    try {
      const [ov, pl, en, es, co, fi, fin, po, ci, sa] = await Promise.all([
        api.get("/m5admin/overview"),
        api.get("/m5admin/plans"),
        api.get("/m5admin/entitlements", { params: { limit: 100 } }),
        api.get("/m5admin/entitlements/summary", { params: { limit: 200 } }),
        api.get("/admin/llm-usage/by-category"),
        api.get(`/m5admin/finance/model/${period}`),
        api.get("/m5admin/finance/inputs", { params: { period } }),
        api.get("/m5admin/content/policies"),
        api.get("/m5admin/content/versions"),
        api.get("/m5admin/sample"),
      ]);
      setOverview(ov.data); setPlans(pl.data.plans || []); setEnts(en.data.entitlements || []);
      setEntSummary(es.data.rows || []);
      setCosts(co.data); setFinance(fi.data); setFinanceInputs(fin.data.inputs || []);
      setPolicies(po.data.policies || []); setContentInfo(ci.data); setSample(sa.data);
    } catch (err) {
      setError(extractApiError(err, "Failed to load admin data."));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, [period]);

  const upsertPlan = async (userId, planId) => {
    try {
      await api.put("/m5admin/entitlements", { user_id: userId, plan_id: planId, overrides: {} });
      toast.success("Entitlement updated. Audit-log entry written.");
      load();
    } catch (err) {
      toast.error(extractApiError(err, "Update failed."));
    }
  };

  const upsertInput = async () => {
    try {
      await api.put("/m5admin/finance/inputs", {
        category: newInput.category, period,
        value: parseFloat(newInput.value) || 0,
        note: newInput.note || null,
      });
      setNewInput({ category: newInput.category, value: "", note: "" });
      toast.success("Manual input saved.");
      load();
    } catch (err) {
      toast.error(extractApiError(err, "Save failed."));
    }
  };

  const publishPolicy = async () => {
    try {
      await api.post("/m5admin/content/policies", newPolicy);
      setNewPolicy({ policy_type: "terms", version: "", content: "" });
      toast.success("Policy version published.");
      load();
    } catch (err) {
      toast.error(extractApiError(err, "Publish failed."));
    }
  };

  const createSample = async () => {
    try { await api.post("/m5admin/sample/create"); toast.success("SAMPLE participant created."); load(); }
    catch (err) { toast.error(extractApiError(err, "Failed.")); }
  };
  const removeSample = async () => {
    try { await api.post("/m5admin/sample/remove"); toast.success("SAMPLE removed."); load(); }
    catch (err) { toast.error(extractApiError(err, "Failed.")); }
  };

  if (loading) {
    return <AppShell><div className="mx-auto max-w-3xl px-4 py-16 text-center text-fg-secondary" data-testid="m5admin-loading">Loading admin workspace…</div></AppShell>;
  }
  if (error) {
    return <AppShell><div className="mx-auto max-w-3xl px-4 py-14"><Alert variant="destructive"><AlertDescription data-testid="m5admin-error">{error}</AlertDescription></Alert></div></AppShell>;
  }

  return (
    <AppShell>
      <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
        <div className="mb-4 flex flex-wrap items-baseline justify-between gap-3">
          <div>
            <p className="text-xs uppercase tracking-wider text-fg-secondary">Admin (M5)</p>
            <h1 className="mt-1 text-3xl sm:text-4xl font-heading font-medium text-fg-primary" data-testid="m5admin-title">
              Operations, entitlements, financial model
            </h1>
            <p className="mt-2 text-xs text-fg-secondary">
              Aggregate-first, narrative-free, SAMPLE-excluded. All sensitive
              actions are audit-logged.
            </p>
          </div>
          <Link to="/admin"><Button variant="ghost" size="sm" data-testid="m5admin-legacy-link" className="text-fg-secondary hover:text-fg-primary hover:bg-bg-surface-hover">
            <ArrowLeft className="h-3.5 w-3.5 mr-2" aria-hidden="true" />
            Legacy admin
          </Button></Link>
          <Link to="/admin/products"><Button variant="ghost" size="sm" data-testid="m5admin-products-link" className="text-fg-secondary hover:text-fg-primary hover:bg-bg-surface-hover">
            <Package className="h-3.5 w-3.5 mr-2" aria-hidden="true" />
            Products & grants
          </Button></Link>
        </div>

        {/* Overview */}
        <Section id="overview" title="Overview" icon={BarChart3}>
          {overview && (
            <>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <Stat testid="stat-total" label="Participants (excl. SAMPLE)" value={overview.participants.total} />
                <Stat testid="stat-onboarded" label="Onboarded" value={overview.participants.onboarded} />
                <Stat testid="stat-submitted" label="Submitted assessment" value={overview.participants.submitted_assessment} />
                <Stat testid="stat-confirmed" label="Confirmed direction" value={overview.directions.users_with_confirmed_direction} />
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-3">
                <Stat testid="stat-foundations" label="Foundations completions" value={overview.learning.foundations_module_completions} />
                <Stat testid="stat-pathway" label="Pathway completions" value={overview.learning.pathway_module_completions} />
                <Stat testid="stat-projects" label="Completed projects" value={overview.projects.completed} />
                <Stat testid="stat-visible" label="Portfolio-visible" value={overview.projects.visible_in_portfolios} />
              </div>
              <p className="mt-2 text-xs text-fg-muted italic">
                SAMPLE participant is excluded from every count above.
              </p>
            </>
          )}
        </Section>

        {/* AI cost by category */}
        <Section id="ai-costs" title="AI cost by task category" icon={Sparkles}>
          {costs && (
            <>
              <div className="rounded-lg border border-border-subtle bg-bg-surface p-4">
                <p className="text-sm text-fg-primary" data-testid="ai-total-cost">
                  Total estimated cost: <span className="text-accent-warm">${(costs.total_estimated_cost || 0).toFixed(6)}</span> across <strong>{costs.total_calls}</strong> non-cached calls.
                </p>
                <p className="mt-1 text-xs text-fg-muted italic">{costs.note}</p>
              </div>
              <div className="mt-3 overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Category</TableHead>
                      <TableHead>Calls</TableHead>
                      <TableHead>Cache hits</TableHead>
                      <TableHead>Tokens</TableHead>
                      <TableHead>Est. cost (USD)</TableHead>
                      <TableHead>Avg latency (ms)</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(costs.by_category || []).map((c) => (
                      <TableRow key={c.task_category} data-testid={`ai-cost-row-${c.task_category}`}>
                        <TableCell>{c.task_category}</TableCell>
                        <TableCell>{c.call_count}</TableCell>
                        <TableCell>{c.cache_hits}</TableCell>
                        <TableCell>{c.input_tokens + c.output_tokens}</TableCell>
                        <TableCell>${(c.estimated_cost || 0).toFixed(6)}</TableCell>
                        <TableCell>{c.avg_latency_ms}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </>
          )}
        </Section>

        {/* Financial model */}
        <Section id="finance" title="Financial model" icon={DollarSign}>
          <div className="flex flex-wrap items-center gap-2 mb-3">
            <label className="text-xs text-fg-secondary">Period</label>
            <Input type="month" value={period} onChange={(e) => setPeriod(e.target.value)}
                    data-testid="finance-period" className="max-w-40 bg-bg-base border-border-subtle" />
          </div>
          {finance && (
            <div className="rounded-lg border border-border-subtle bg-bg-surface p-4">
              <p className="text-xs uppercase tracking-wider text-fg-secondary">Net profit (period {finance.period})</p>
              <p className="mt-1 text-3xl font-heading font-medium text-fg-primary" data-testid="finance-net-profit">
                ${finance.net_profit?.toFixed(2)}
              </p>
              <p className="mt-1 text-xs text-fg-muted italic" data-testid="finance-formula">
                Formula: {finance.net_profit_formula}
              </p>
              <p className="mt-1 text-xs text-fg-muted italic">{finance.billing_note}</p>
              <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-2 text-sm">
                {Object.entries(finance.manual_inputs || {}).map(([k, v]) => (
                  <div key={k} data-testid={`finance-manual-${k}`}>
                    <p className="text-[11px] text-fg-secondary">{k}</p>
                    <p className="text-fg-primary">${(v || 0).toFixed(2)} <span className="text-[10px] text-fg-muted">manual</span></p>
                  </div>
                ))}
                <div data-testid="finance-ai-estimated">
                  <p className="text-[11px] text-fg-secondary">ai_usage</p>
                  <p className="text-fg-primary">${(finance.ai_usage_estimated || 0).toFixed(6)} <span className="text-[10px] text-fg-muted">estimate</span></p>
                </div>
              </div>
              <div className="mt-3 grid grid-cols-2 md:grid-cols-3 gap-2 text-sm">
                <div data-testid="finance-cac">
                  <p className="text-[11px] text-fg-secondary">CAC</p>
                  <p className="text-fg-primary">
                    {finance.cac === null ? <em className="text-fg-muted">{finance.cac_note}</em> : `$${finance.cac?.toFixed(2)}`}
                  </p>
                </div>
                <div data-testid="finance-margin">
                  <p className="text-[11px] text-fg-secondary">Contribution margin</p>
                  <p className="text-fg-primary">${finance.contribution_margin?.toFixed(2)}</p>
                </div>
              </div>
            </div>
          )}
          <div className="mt-4 rounded-lg border border-border-subtle bg-bg-surface p-4">
            <p className="text-sm text-fg-primary font-medium">Add manual input for period {period}</p>
            <div className="mt-2 flex flex-wrap gap-2 items-end">
              <select
                data-testid="finance-input-category"
                className="rounded-md border border-border-subtle bg-bg-base px-3 py-2 text-sm text-fg-primary"
                value={newInput.category}
                onChange={(e) => setNewInput((s) => ({ ...s, category: e.target.value }))}
              >
                {["revenue", "discounts_trials", "store_payment_fees", "taxes",
                  "infrastructure", "development_content", "support",
                  "refunds_chargebacks", "reserves", "marketing_spend",
                  "paying_subscribers"].map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
              <Input type="number" step="0.01" placeholder="value"
                     data-testid="finance-input-value" className="max-w-32 bg-bg-base border-border-subtle"
                     value={newInput.value}
                     onChange={(e) => setNewInput((s) => ({ ...s, value: e.target.value }))}/>
              <Input placeholder="note (optional)"
                     data-testid="finance-input-note" className="flex-1 min-w-40 bg-bg-base border-border-subtle"
                     value={newInput.note}
                     onChange={(e) => setNewInput((s) => ({ ...s, note: e.target.value }))}/>
              <Button size="sm" onClick={upsertInput} data-testid="finance-input-save"
                       className="bg-accent-warm text-bg-base hover:bg-accent-warm-hover">
                <Plus className="h-3.5 w-3.5 mr-2" aria-hidden="true" />
                Save
              </Button>
            </div>
            <p className="mt-2 text-xs text-fg-muted italic">
              Each row records the admin who entered it and the timestamp. Values are labeled "manual".
            </p>
            {financeInputs.length > 0 && (
              <details className="mt-3">
                <summary className="text-xs text-fg-muted cursor-pointer">All {financeInputs.length} inputs for {period}</summary>
                <ul className="mt-2 text-xs text-fg-secondary space-y-1" data-testid="finance-inputs-list">
                  {financeInputs.map((r) => (
                    <li key={r.id}>
                      {r.category}: ${r.value?.toFixed(2)}  ·  entered by {r.entered_by}  ·  {new Date(r.entered_at).toLocaleString()}
                      {r.note && ` — ${r.note}`}
                    </li>
                  ))}
                </ul>
              </details>
            )}
          </div>
        </Section>

        {/* Entitlements */}
        <Section id="entitlements" title="Entitlement plans & assignments" icon={Users}>
          <div className="rounded-lg border border-border-subtle bg-bg-surface p-4">
            <p className="text-sm text-fg-primary font-medium">Plans</p>
            <ul className="mt-2 text-xs text-fg-secondary space-y-1" data-testid="plans-list">
              {plans.map((p) => (
                <li key={p.id} data-testid={`plan-${p.id}`}>
                  <strong>{p.name}</strong> ({p.id}) — <em>{p.label}</em>{p.is_dev && " · DEV"}
                </li>
              ))}
            </ul>
          </div>
          <div className="mt-4 rounded-lg border border-border-subtle bg-bg-surface p-4">
            <p className="text-sm text-fg-primary font-medium">
              User entitlements — resolved for every real user ({entSummary.length})
            </p>
            <p className="text-xs text-fg-secondary mt-1">
              <code className="rounded bg-bg-base px-1 py-0.5 text-[10px] text-progress-teal">explicit</code>{" "}
              = an on-record entitlement row; {" "}
              <code className="rounded bg-bg-base px-1 py-0.5 text-[10px] text-accent-warm">default_plan</code>{" "}
              = no row on record, resolved from the DEV plan in memory
              (nothing persisted just to fill this table). Assigning a plan
              here materialises a row.
            </p>
            <div className="mt-2 overflow-x-auto max-h-80 overflow-y-auto">
              <Table>
                <TableHeader><TableRow>
                  <TableHead>User</TableHead>
                  <TableHead>Plan</TableHead>
                  <TableHead>Source</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {entSummary.map((e) => (
                    <TableRow key={e.user_id} data-testid={`ent-row-${e.user_id}`}>
                      <TableCell>
                        <div className="text-xs text-fg-primary">{e.email}</div>
                        <code className="text-[10px] text-fg-secondary">{e.user_id}</code>
                      </TableCell>
                      <TableCell className="text-xs">{e.plan_id}</TableCell>
                      <TableCell>
                        <span
                          data-testid={`ent-source-${e.user_id}`}
                          className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] uppercase tracking-wider ${e.source === "explicit"
                             ? "bg-progress-teal/15 text-progress-teal border-progress-teal/40"
                             : "bg-accent-warm/15 text-accent-warm border-accent-warm/40"}`}
                        >{e.source}</span>
                      </TableCell>
                      <TableCell>
                        <select
                          data-testid={`ent-select-${e.user_id}`}
                          className="rounded-md border border-border-subtle bg-bg-base px-2 py-1 text-xs text-fg-primary"
                          defaultValue={e.plan_id}
                          onChange={(ev) => upsertPlan(e.user_id, ev.target.value)}
                        >
                          {plans.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                        </select>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        </Section>

        {/* Content & policies */}
        <Section id="content" title="Content & policies" icon={FileText}>
          {contentInfo && (
            <div className="rounded-lg border border-border-subtle bg-bg-surface p-4" data-testid="content-info">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="text-sm text-fg-primary">
                    Course content version: <strong>{contentInfo.current_version || "—"}</strong>
                  </p>
                  <p className="text-xs text-fg-secondary">
                    Versions on record: {(contentInfo.versions || []).length}
                    {(contentInfo.versions || [])
                       .filter((v) => v.state === "draft").length > 0 && (
                       <span> — {(contentInfo.versions || []).filter((v) => v.state === "draft").length} draft(s)</span>
                    )}
                  </p>
                  <p className="mt-2 text-xs text-fg-muted italic">{contentInfo.notice}</p>
                </div>
                <Link to="/admin/content" data-testid="content-open-editor-link">
                  <Button variant="outline" size="sm">
                    Open course content editor
                  </Button>
                </Link>
              </div>
            </div>
          )}
          <div className="mt-4 rounded-lg border border-border-subtle bg-bg-surface p-4">
            <p className="text-sm text-fg-primary font-medium">Policy versions (audit-logged on publish)</p>
            <ul className="mt-2 text-xs text-fg-secondary space-y-1" data-testid="policies-list">
              {policies.map((p) => (
                <li key={p.id}><strong>{p.policy_type}</strong> v{p.version} — effective {p.effective_at?.slice(0, 10)}</li>
              ))}
            </ul>
            <div className="mt-3 space-y-2">
              <select data-testid="policy-type"
                       className="rounded-md border border-border-subtle bg-bg-base px-3 py-2 text-sm text-fg-primary"
                       value={newPolicy.policy_type}
                       onChange={(e) => setNewPolicy((s) => ({ ...s, policy_type: e.target.value }))}>
                <option value="terms">terms</option>
                <option value="privacy">privacy</option>
                <option value="ai_processing">ai_processing</option>
              </select>
              <Input placeholder="version (e.g. 1.1)" data-testid="policy-version"
                       className="max-w-40 bg-bg-base border-border-subtle"
                       value={newPolicy.version}
                       onChange={(e) => setNewPolicy((s) => ({ ...s, version: e.target.value }))}/>
              <Textarea placeholder="content (markdown)" data-testid="policy-content"
                          className="bg-bg-base border-border-subtle text-fg-primary min-h-24"
                          value={newPolicy.content}
                          onChange={(e) => setNewPolicy((s) => ({ ...s, content: e.target.value }))}/>
              <Button size="sm" onClick={publishPolicy} data-testid="policy-publish-btn"
                       className="bg-accent-warm text-bg-base hover:bg-accent-warm-hover">
                Publish new policy version
              </Button>
            </div>
          </div>
        </Section>

        {/* SAMPLE participant */}
        <Section id="sample" title="SAMPLE participant" icon={Beaker}>
          <div className="rounded-lg border border-border-subtle bg-bg-surface p-4" data-testid="sample-block">
            {sample?.exists ? (
              <>
                <p className="text-sm text-fg-primary">
                  <span className="text-[11px] uppercase tracking-wider text-accent-warm mr-2">SAMPLE</span>
                  {sample.sample.display_name} · <code className="text-xs">{sample.sample.id}</code>
                </p>
                <p className="mt-1 text-xs text-fg-muted italic">
                  Not loginable. Excluded from every aggregate. Never counted in financial or conversion stats.
                </p>
                <Button size="sm" variant="destructive" onClick={removeSample} data-testid="sample-remove-btn" className="mt-3">
                  Remove SAMPLE
                </Button>
              </>
            ) : (
              <>
                <p className="text-sm text-fg-secondary">No SAMPLE participant exists.</p>
                <Button size="sm" onClick={createSample} data-testid="sample-create-btn"
                         className="mt-3 bg-accent-warm text-bg-base hover:bg-accent-warm-hover">
                  Create SAMPLE participant
                </Button>
              </>
            )}
          </div>
        </Section>
      </div>
    </AppShell>
  );
}
